import {
  Children,
  cloneElement,
  useState,
  useEffect,
  ReactNode,
  ReactElement,
  CSSProperties,
  ComponentType,
} from 'react'
import { noop } from 'lodash'
import {
  useFloating,
  flip,
  shift,
  autoUpdate,
  useInteractions,
  useClick,
  useDismiss,
  FloatingPortal,
} from '@floating-ui/react'

interface PopoverProps {
  children: ReactNode
  placement?:
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'right'
    | 'right-start'
    | 'right-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'left'
    | 'left-start'
    | 'left-end'
  controlVisibility?: boolean | null
  triggerOnReferenceClick?: boolean
  onDismiss?: () => void
  onOpen?: () => void
  middleware?: any[]
}

interface PopoverReferenceProps {
  className?: string
  children: ReactNode
  refs?: {
    setReference: (node: HTMLElement | null) => void
  }
  getReferenceProps?: () => Record<string, any>
  onTrigger?: (() => void) | null
}

const PopoverReference = ({
  children,
  className,
  refs,
  getReferenceProps,
  onTrigger = null,
}: PopoverReferenceProps) => {
  const childCount = Children.count(children)
  let referenceClickHandler = noop
  let content = children

  if (onTrigger !== null) {
    if (childCount === 0) {
      return null
    } else if (childCount > 1) {
      referenceClickHandler = onTrigger
      content = children
    } else {
      const childrenArray = Children.toArray(children)
      let child = childrenArray[0] as ReactElement
      if (child.props && (child.props.onClick === null || child.props.onClick === noop)) {
        child = cloneElement(child, {
          onClick: onTrigger,
        })
      }
      content = child
    }
  }

  const refProps = getReferenceProps && getReferenceProps()
  return (
    <span ref={refs?.setReference} onClick={referenceClickHandler} {...refProps} className={className}>
      {content}
    </span>
  )
}

PopoverReference.displayName = 'Popover.Reference'

interface PopoverContentProps {
  className?: string
  children: ReactNode
  refs?: {
    setFloating: (node: HTMLElement | null) => void
  }
  floatingStyles?: CSSProperties
  getFloatingProps?: () => Record<string, any>
  popoverVisible?: boolean
  renderInPortal?: boolean
}

const PopoverContent = ({
  children,
  className = 'popover popover--opaque p',
  refs,
  floatingStyles,
  getFloatingProps,
  popoverVisible = false,
  renderInPortal = true,
}: PopoverContentProps) => {
  const flProps = getFloatingProps && getFloatingProps()
  const content = (
    <div ref={refs?.setFloating} style={floatingStyles} {...flProps} className={className}>
      {children}
    </div>
  )

  if (popoverVisible) {
    if (renderInPortal) {
      return <FloatingPortal id="floating-portal-target">{content}</FloatingPortal>
    }
    return content
  }
  return null
}

PopoverContent.displayName = 'Popover.Content'

type PopoverComponent = ComponentType<PopoverProps> & {
  Reference: ComponentType<PopoverReferenceProps>
  Content: ComponentType<PopoverContentProps>
}

const Popover: PopoverComponent = ({
  children,
  placement = 'bottom',
  controlVisibility = null,
  triggerOnReferenceClick = true,
  onDismiss,
  onOpen,
  middleware = [
    flip(),
    shift({
      padding: 16, // 16px from window edges
    }),
  ],
}: PopoverProps) => {
  const [popoverVisible, setPopoverVisible] = useState<boolean>(false)

  useEffect(() => {
    if (controlVisibility !== null) {
      setPopoverVisible(controlVisibility)
    }
  }, [controlVisibility])

  const handleVisibleChange = (isVisible: boolean) => {
    setPopoverVisible(isVisible)
    if (!isVisible && onDismiss) {
      onDismiss()
    }
    if (isVisible && onOpen) {
      onOpen()
    }
  }

  const { refs, floatingStyles, context } = useFloating({
    open: popoverVisible,
    onOpenChange: handleVisibleChange,
    placement,
    middleware,
    whileElementsMounted: autoUpdate,
  })
  const { getReferenceProps, getFloatingProps } = useInteractions([useClick(context), useDismiss(context)])

  const handleTrigger = () => {
    handleVisibleChange(!popoverVisible)
  }

  return (
    <>
      {Children.map(children, child =>
        cloneElement(child as ReactElement, {
          refs,
          floatingStyles,
          getFloatingProps,
          getReferenceProps,
          popoverVisible,
          onTrigger: triggerOnReferenceClick ? handleTrigger : null,
        })
      )}
    </>
  )
}

Popover.Reference = PopoverReference
Popover.Content = PopoverContent

export default Popover
